import type { DepositAccount } from '@fruitful/types';
import * as React from 'react';
import {
    BadgeLabel,
    BodyMediumEmphasized,
    BodyMediumRegular,
    ChevronRightIcon,
    FootnoteRegular,
    FruitfulCustomIcon,
    Icon,
    InstitutionLogo,
    NavigationRow,
    Private,
    Row,
    type StackProps,
    XStack,
    YStack,
} from 'smoothie';

import { isEmpty } from '../../../common/misc';
import {
    centsToDollars,
    formatDollars,
    getNormalizedDepositAccountNickname,
} from '../../../common/utils/bankingUtils';
import { FRUITFUL_INSTITUTION_NAME } from '../../types/BankAccount';

interface TransferAccountLineItemProps {
    onPress?: () => void;
    depositAccount?: DepositAccount | null;
    placeholder?: string;
    showActionIcon?: boolean;
}

export const SelectTransferAccountLineItem = ({
    depositAccount,
    onPress,
    placeholder,
    showActionIcon,
}: TransferAccountLineItemProps) => {
    const {
        imgBase64,
        logo,
        icon,
        color,
        bankName,
        accountNumberMasked,
        availableBalance,
        isExternal,
        verified,
    } = depositAccount ?? {};

    const inReview = isExternal && !verified;

    const leftContentOverride =
        bankName !== FRUITFUL_INSTITUTION_NAME ? (
            <InstitutionLogo logo={logo ?? imgBase64} />
        ) : (
            <FruitfulCustomIcon icon={icon} color={color} />
        );

    const acctNo =
        accountNumberMasked != null ? `* ${accountNumberMasked}` : undefined;
    const formattedBalance =
        availableBalance != null
            ? ` • ${formatDollars(centsToDollars(availableBalance))}`
            : '';
    const subtitle = `${bankName} ${acctNo} ${formattedBalance}`;

    const sharedContainerProps: StackProps = {
        backgroundColor: '$gray100',
        borderBottomLeftRadius: 24,
        borderBottomRightRadius: 24,
    };

    return showActionIcon ? (
        <NavigationRow
            containerProps={sharedContainerProps}
            title={
                depositAccount != null
                    ? getNormalizedDepositAccountNickname(depositAccount)
                    : (placeholder ?? '')
            }
            {...(inReview ? { titleProps: { color: '$gray700' } } : {})}
            subtitle={depositAccount != null ? subtitle : undefined}
            {...(inReview ? { subtitleProps: { color: '$gray600' } } : {})}
            {...(leftContentOverride != null ? { leftContentOverride } : {})}
            onPress={onPress}
            disabled={inReview}
        />
    ) : (
        <Row
            containerProps={sharedContainerProps}
            title={
                depositAccount != null
                    ? getNormalizedDepositAccountNickname(depositAccount)
                    : (placeholder ?? '')
            }
            subtitle={depositAccount != null ? subtitle : undefined}
            {...(leftContentOverride != null ? { leftContentOverride } : {})}
            onPress={onPress}
        />
    );
};

const TransferAccountLineItem = ({
    depositAccount,
    onPress,
    placeholder,
    showActionIcon,
}: TransferAccountLineItemProps) => {
    const {
        imgBase64,
        logo,
        icon,
        color,
        bankName,
        accountNumberMasked,
        availableBalance,
        verified,
        isExternal,
    } = depositAccount ?? {};

    const inReview = isExternal && !verified;

    return (
        <XStack
            alignItems="center"
            justifyContent="space-between"
            gap={16}
            onPress={onPress}
            cursor="pointer"
            hoverStyle={{
                ...(!inReview ? { backgroundColor: '$gray100' } : {}),
                cursor: inReview ? 'text' : 'pointer',
            }}
            padding={16}
            borderRadius={24}
            disabled={inReview}
            {...(inReview != null ? { backgroundColor: '$gray200' } : {})}
        >
            <XStack
                alignItems="center"
                gap={16}
                flexShrink={1}
                $platform-web={{ flexGrow: 1 }}
            >
                {bankName === FRUITFUL_INSTITUTION_NAME ? (
                    <FruitfulCustomIcon icon={icon} color={color} />
                ) : (
                    <InstitutionLogo logo={logo ?? imgBase64} />
                )}

                {depositAccount != null ? (
                    <XStack
                        flex={1}
                        alignItems="center"
                        justifyContent="space-between"
                    >
                        <YStack flex={1}>
                            <BodyMediumRegular
                                {...(inReview ? { color: '$gray700' } : {})}
                            >
                                {getNormalizedDepositAccountNickname(
                                    depositAccount,
                                )}
                            </BodyMediumRegular>
                            <XStack alignItems="center" gap={8}>
                                {(!isEmpty(bankName) ||
                                    !isEmpty(accountNumberMasked)) && (
                                    <FootnoteRegular
                                        color={
                                            inReview ? '$gray600' : '$gray1000'
                                        }
                                    >
                                        {bankName} *{accountNumberMasked}
                                    </FootnoteRegular>
                                )}
                            </XStack>
                        </YStack>
                        {inReview && (
                            <BadgeLabel variant="neutral" text="In review" />
                        )}
                        <Private>
                            <BodyMediumEmphasized>
                                {availableBalance != null
                                    ? formatDollars(
                                          centsToDollars(availableBalance),
                                      )
                                    : ''}
                            </BodyMediumEmphasized>
                        </Private>
                    </XStack>
                ) : (
                    <BodyMediumEmphasized>{placeholder}</BodyMediumEmphasized>
                )}
            </XStack>
            {showActionIcon && <Icon icon={ChevronRightIcon} size={16} />}
        </XStack>
    );
};

export default TransferAccountLineItem;
